import { z } from 'zod';
import { moneySchema } from '../money';

export const stockMovementTypeSchema = z.enum(['IN', 'OUT', 'ADJUST']);
export type StockMovementType = z.infer<typeof stockMovementTypeSchema>;

/**
 * Herkunft einer Lagerbewegung (Belegfluss-Rückverfolgung) — welcher Beleg hat
 * sie ausgelöst. PICKING/DELIVERY_NOTE/GOODS_RECEIPT/PRODUCTION verweisen per
 * `sourceId` auf den Beleg; CORRECTION/MANUAL sind belegfreie manuelle Buchungen.
 */
export const stockMovementSourceSchema = z.enum([
  'PICKING',
  'DELIVERY_NOTE',
  'GOODS_RECEIPT',
  'PRODUCTION',
  'CORRECTION',
  'MANUAL',
]);
export type StockMovementSource = z.infer<typeof stockMovementSourceSchema>;

/** Anzeige-Label je Herkunft (DE) für Tabellen/Detailansichten. */
export const SOURCE_LABELS: Record<StockMovementSource, string> = {
  PICKING: 'Kommissionierung',
  DELIVERY_NOTE: 'Lieferschein',
  GOODS_RECEIPT: 'Wareneingang',
  PRODUCTION: 'Fertigung',
  CORRECTION: 'Korrektur',
  MANUAL: 'Manuell',
};

/**
 * Frontend-Route zum auslösenden Beleg je Herkunft. `:id` wird durch `sourceId`
 * ersetzt. `null` = kein verlinkbares Ziel (belegfreie Buchung). Der Belegfluss-
 * Link im Frontend nutzt diese Zuordnung.
 */
export const SOURCE_ROUTES: Record<StockMovementSource, string | null> = {
  PICKING: '/kommissionierung/:id',
  DELIVERY_NOTE: '/verkauf/:id',
  GOODS_RECEIPT: '/beschaffung/:id',
  PRODUCTION: null, // noch keine Detailroute im Frontend
  // Nur über eine Inventur gebuchte Korrekturen haben eine sourceId (ADR 0058)
  // und verlinken damit; einzelne manuelle Korrekturen (sourceId null) bleiben
  // ohne Link (movementSourceLink prüft sourceId zusätzlich zum Template).
  CORRECTION: '/einkauf/inventur/:id',
  MANUAL: null,
};

/**
 * Listen-Query der Bewegungs-Übersicht `GET /v1/stock-movements` (§8): paginiert
 * mit `page`/`pageSize` (Defaults 1/500, `pageSize` max. 500) plus optionale
 * Filter `articleId`/`projectId`. `coerce`, weil Query-Parameter Strings sind.
 */
export const listStockMovementsQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(500).default(500),
  articleId: z.string().uuid().optional(),
  projectId: z.string().uuid().optional(),
});
export type ListStockMovementsQuery = z.infer<typeof listStockMovementsQuerySchema>;

export const createStockMovementSchema = z.object({
  type: stockMovementTypeSchema,
  /** Menge (Betrag). Bei ADJUST der neue Absolut-Bestand. */
  quantity: z.number().min(0),
  note: z.string().trim().max(500).optional(),
  occurredAt: z.string().datetime().optional(),
});
export type CreateStockMovementInput = z.infer<typeof createStockMovementSchema>;

export const stockMovementResponseSchema = z.object({
  id: z.string().uuid(),
  articleId: z.string().uuid(),
  type: stockMovementTypeSchema,
  quantity: z.number(),
  note: z.string().nullable(),
  occurredAt: z.string().datetime(),
  /** Projektbezug der Bewegung (null = projektneutral). */
  projectId: z.string().uuid().nullable(),
  projectCode: z.string().nullable(),
  /** EK-Snapshot zum Bewegungszeitpunkt (ADR 0016) — null, wenn kein EK gepflegt war. */
  unitCost: moneySchema.nullable(),
  /** Herkunft der Bewegung (Belegfluss). null = Altbestand ohne Herkunft. */
  sourceType: stockMovementSourceSchema.nullable(),
  /** Soft-Ref auf den auslösenden Beleg (kein FK). null = belegfrei/Altbestand. */
  sourceId: z.string().uuid().nullable(),
  /** Beleg-Nummer-Snapshot (z. B. „KO-2026-0042"). null = belegfrei/Altbestand. */
  sourceRef: z.string().nullable(),
  createdAt: z.string().datetime(),
});
export type StockMovementResponse = z.infer<typeof stockMovementResponseSchema>;

/** Zeile der Artikelbewegungs-Übersicht (nach Artikel & Projekt). */
export const stockMovementOverviewItemSchema = z.object({
  id: z.string().uuid(),
  articleId: z.string().uuid(),
  articleNumber: z.string(),
  articleName: z.string(),
  unit: z.string().nullable(),
  type: stockMovementTypeSchema,
  quantity: z.number(),
  projectId: z.string().uuid().nullable(),
  projectCode: z.string().nullable(),
  note: z.string().nullable(),
  occurredAt: z.string().datetime(),
  /** Herkunft der Bewegung (Belegfluss). null = Altbestand ohne Herkunft. */
  sourceType: stockMovementSourceSchema.nullable(),
  /** Soft-Ref auf den auslösenden Beleg (kein FK). null = belegfrei/Altbestand. */
  sourceId: z.string().uuid().nullable(),
  /** Beleg-Nummer-Snapshot (z. B. „KO-2026-0042"). null = belegfrei/Altbestand. */
  sourceRef: z.string().nullable(),
});
export type StockMovementOverviewItem = z.infer<typeof stockMovementOverviewItemSchema>;

/**
 * Aktive Lager-Reservierung eines Artikels: „reserviert für Projekt …" (Beschaffungs-
 * Spine) oder „reserviert für Fertigungsauftrag …" (Produktions-Freigabe).
 */
export const stockReservationResponseSchema = z.object({
  id: z.string().uuid(),
  articleId: z.string().uuid(),
  projectId: z.string().uuid().nullable(),
  projectCode: z.string().nullable(),
  /** Gesetzt, wenn die Reservierung aus der Freigabe eines Fertigungsauftrags stammt. */
  productionOrderId: z.string().uuid().nullable(),
  quantity: z.number(),
  createdAt: z.string().datetime(),
});
export type StockReservationResponse = z.infer<typeof stockReservationResponseSchema>;
